import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { 
  usePost as usePostQuery, 
  useCreatePost as useCreatePostMutation,
  useDeletePost,
  Post,
  queryKeys,
  apiClient
} from '../lib/api';

// Re-export the hooks from api.ts for backward compatibility
export { useCreatePostMutation as useCreatePost, usePostQuery as usePost, useDeletePost };

// Enhanced infinite query hook for posts with pagination.
// tagNames: filter by one or more tags (OR).
// feed: 'following' = home (followed users + online.khabar); omit/all = explorer/global.
export const usePosts = (
  tagNames?: string[] | string | null,
  type?: string,
  feed?: 'following' | 'all'
) => {
  const tags = Array.isArray(tagNames)
    ? tagNames.filter(Boolean).map((t) => String(t).trim().toLowerCase())
    : tagNames
      ? [String(tagNames).trim().toLowerCase()]
      : undefined;
  const hasTags = tags && tags.length > 0;
  const followingFeed = feed === 'following';

  return useInfiniteQuery({
    queryKey: hasTags
      ? [...queryKeys.postsByTags(tags), 'infinite', type || 'all']
      : [...queryKeys.posts, 'infinite', type || 'all', followingFeed ? 'following' : 'all'],
    staleTime: 2 * 60 * 1000,
    queryFn: async ({ pageParam = 1 }) => {
      let data;
      if (hasTags) {
        const tagParams = tags!.map((t) => `tag=${encodeURIComponent(t)}`).join('&');
        const response = await apiClient.get(`/posts/by-tags?${tagParams}&page=${pageParam}&limit=3&include_details=true`);
        data = response.data;
      } else {
        const typeParam = type && type !== 'all' ? `&type=${type}` : '';
        const feedParam = followingFeed ? '&feed=following' : '';
        const response = await apiClient.get(
          `/posts?page=${pageParam}&limit=3&include_details=true${typeParam}${feedParam}`
        );
        data = response.data;
      }
      return {
        posts: data,
        nextPage: data.length === 3 ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
  });
};

// Simple hook that returns a post from cache
export const usePostFromCache = (id: string) => {
  const queryClient = useQueryClient();
  return queryClient.getQueryData(queryKeys.post(id)) as Post | undefined;
}; 